/**
 * What has been imported, and the way back out of it.
 *
 * **This is the answer to the one mistake the rest of the transfer screen cannot fix.** Everything
 * else there is built on a file being read, reported on and corrected before it is written — and
 * that catches the bad *rows*. It does not catch the bad *file*: the right columns, every row
 * perfectly valid, and two hundred buildings that belong to somebody else. Re-running a corrected
 * file will never remove those, because nothing in the corrected file matches them.
 *
 * So an import is a thing with a name and a date that can be taken back, and taking it back is the
 * same two steps as making it: a report of exactly what would happen, then a press.
 *
 * **What it will not do matters as much as what it will.** A record somebody has edited since the
 * import is left alone, and so is one that has been built on. Both are listed by the line of the
 * file they came from, with the reason. The worst this can do is less than was asked.
 *
 * ## Collapsed, and it fetches nothing until it is opened
 *
 * The transfer screen is opened to *do* an import or an export. The history of past ones is
 * reference material — wanted rarely, and urgently when it is wanted. Closed by default it costs a
 * heading; open it costs a request. So the request is not made until somebody asks for it: the
 * query is disabled while the panel is shut, which means the common visit to this screen no longer
 * pays for a list nobody looked at.
 *
 * A `<button>` with `aria-expanded` and `aria-controls`, not a bare `<div>` with a click handler —
 * this is a disclosure, and a screen reader has to be told it is one and whether it is open.
 *
 * ## Behind the plan, and drawn anyway
 *
 * Undoing an import is part of `data_transfer`, which is a paid feature (ADR-0091). The panel is
 * still **drawn**, with the padlock, exactly as the export and import panels above it are — ADR-0114
 * is explicit that a plan limit is shown rather than hidden, because a feature nobody can see is a
 * feature nobody buys.
 *
 * **This reverses ADR-0152**, which left the undo unpadlocked so that an agency whose plan lapsed
 * after a bad import would not be told that fixing it cost money. The operator decided otherwise on
 * 31 August. The consequence is worth stating plainly rather than burying: an agency that imports
 * badly and then drops to the free plan can no longer take that import back from this screen. The
 * records are all still there and nothing has been deleted — but the way out is now behind the
 * plan, and undoing it by hand is the only alternative.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { pluralise } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useFeatureGate } from "@/lib/gating";
import { Padlock, PadlockButton } from "@/components/padlock";
import { IconChevronDown } from "@/components/icons";
import { Alert, Badge, Button, Card, EmptyState, ErrorState, Spinner, Table } from "@/components/ui";

interface ImportRun {
  id: string;
  kind: string;
  label: string;
  createdAt: string;
  by: string | null;
  created: number;
  updated: number;
  failed: number;
  rows: number;
  undoneAt: string | null;
  removed: number | null;
  restored: number | null;
  kept: number | null;
}

interface UndoRow {
  line: number;
  kind: string;
  label: string;
  action: "remove" | "restore" | "keep";
  reason: string | null;
}

interface UndoReport {
  run: { id: string; label: string };
  summary: { remove: number; restore: number; keep: number };
  rows: UndoRow[];
  truncated: boolean;
  committed: boolean;
}

const when = (value: string) => new Date(value).toLocaleString();

export function HistoryPanel() {
  const gate = useFeatureGate("data_transfer");
  const [open, setOpen] = useState(false);

  /* Which run is being looked at, and what the server said would happen to it. Held here rather
     than per row, so opening a second report closes the first — two reports on screen at once is
     two "Undo" buttons that look alike and do different things. */
  const [showing, setShowing] = useState<string | null>(null);
  const [report, setReport] = useState<UndoReport | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["data-import-runs"],
    queryFn: () => api.get<{ runs: ImportRun[] }>("/data/imports/runs"),
    /* Nothing is asked for until the panel is open, and nothing at all while it is padlocked —
       a request whose answer cannot be shown is a request not worth making. */
    enabled: open && !gate,
  });

  const undo = useApiMutation<{ id: string; commit: boolean }, UndoReport>({
    mutationFn: (input) => api.post<UndoReport>(`/data/imports/runs/${input.id}/undo`, { commit: input.commit }),
    invalidates: [["data-import-runs"], ["data-summary"]],
    onSuccess: (result, input) => {
      setShowing(input.id);
      setReport(result);
    },
  });

  const runs = data?.runs ?? [];

  return (
    <Card className="mt-5" padded={false}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls="import-history"
        data-runs-toggle
        className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-surface)] px-5 py-4 text-left transition-colors hover:bg-ink-50 sm:px-6"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-base font-semibold text-ink-900">What has been imported</span>
          {gate ? <Padlock gate={gate} label="Locked" /> : null}
        </span>
        <IconChevronDown
          size={18}
          className={`shrink-0 text-ink-500 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div id="import-history" data-runs-panel className="border-t border-ink-200 px-5 py-5 sm:px-6">
          {gate ? (
            /* Drawn, not hidden — the same treatment the panels above get. What is refused is the
               list and the act, never the knowledge that the feature exists. */
            <div className="flex flex-wrap items-center gap-3 text-sm text-ink-600">
              <p className="min-w-0 flex-1">
                {gate.message} Past imports are still recorded and nothing of yours has been removed;
                taking one back is part of import and export, so it moves with the plan.
              </p>
              <PadlockButton gate={gate} variant="primary">
                See the plans
              </PadlockButton>
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-ink-500">
                Only what was actually written is listed. A preview writes nothing, so it is not an
                import and there is nothing to take back.
              </p>

              {isLoading ? <Spinner /> : null}
              {error ? <ErrorState error={error} retry={() => refetch()} /> : null}

              {!isLoading && !error && runs.length === 0 ? (
                <EmptyState
                  title="Nothing has been imported yet"
                  hint="An import you commit is listed here, with a way to take it back."
                />
              ) : null}

              {runs.length > 0 ? (
                <Table
                  head={
                    <tr>
                      <th>What</th>
                      <th>When</th>
                      <th>By</th>
                      <th>Wrote</th>
                      <th>Standing</th>
                      <th />
                    </tr>
                  }
                >
                  {runs.map((run) => (
                    <tr key={run.id} data-run-row={run.id}>
                      <td className="font-medium text-ink-900">{run.label}</td>
                      <td className="text-ink-500">{when(run.createdAt)}</td>
                      <td className="text-ink-500">{run.by ?? "—"}</td>
                      <td className="text-ink-500">
                        {pluralise(run.created, "new record")}, {run.updated} updated
                      </td>
                      <td>
                        {run.undoneAt ? (
                          <Badge tone="muted">
                            Taken back — {run.removed ?? 0} removed, {run.restored ?? 0} put back
                            {run.kept ? `, ${run.kept} left alone` : ""}
                          </Badge>
                        ) : (
                          <Badge tone="success" dot>
                            Standing
                          </Badge>
                        )}
                      </td>
                      <td>
                        {run.undoneAt ? null : (
                          <Button
                            variant="secondary"
                            data-run-undo
                            onClick={() => undo.mutate({ id: run.id, commit: false })}
                            disabled={undo.isPending}
                          >
                            Take it back
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              ) : null}

              {undo.error ? <Alert tone="danger">{(undo.error as Error).message}</Alert> : null}

              {report && showing ? (
                <div className="mt-5 rounded-[var(--radius-surface)] border border-ink-200 p-4" data-undo-report>
                  <h3 className="text-sm font-semibold text-ink-900">
                    {report.committed ? "Taken back" : "What would happen"} — {report.run.label}
                  </h3>
                  <p className="mt-1 text-sm text-ink-600">
                    {report.summary.remove} to remove, {report.summary.restore} to put back,{" "}
                    {report.summary.keep} left exactly as {report.summary.keep === 1 ? "it is" : "they are"}.
                  </p>

                  {report.rows.length ? (
                    <div className="mt-3">
                      <Table
                        head={
                          <tr>
                            <th className="num">Line</th>
                            <th>Record</th>
                            <th>{report.committed ? "What happened" : "What happens"}</th>
                            <th>Why</th>
                          </tr>
                        }
                      >
                        {report.rows.map((row) => (
                          <tr key={`${row.kind}-${row.line}`} data-undo-row={row.line}>
                            <td className="num tabular text-ink-500">{row.line}</td>
                            <td className="font-medium text-ink-900">{row.label || "—"}</td>
                            <td>
                              {row.action === "remove"
                                ? report.committed ? "Removed" : "Removed"
                                : row.action === "restore"
                                  ? "Put back"
                                  : "Left alone"}
                            </td>
                            <td className="text-xs text-ink-500">{row.reason ?? "—"}</td>
                          </tr>
                        ))}
                      </Table>
                      {report.truncated ? (
                        <p className="mt-2 text-xs text-ink-500">
                          The first {pluralise(report.rows.length, "row")} are shown. The counts above
                          cover the whole import.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {/* The destructive press is a second, separate act, and it is the only one that
                      says so. `commit` defaults to false on the server for the same reason. */}
                  {!report.committed ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        variant="danger"
                        data-undo-confirm
                        onClick={() => undo.mutate({ id: showing, commit: true })}
                        disabled={undo.isPending}
                      >
                        Take back this import
                      </Button>
                      <Button variant="secondary" onClick={() => { setShowing(null); setReport(null); }}>
                        Leave it
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </Card>
  );
}
